import {mergeStyles} from 'components/FluentUICompat';
import React from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.bubble.css';
import 'components/quill/registerQuillModules';

type RichtextDisplayProps = {
	value: string;
	limit?: number;
	removeHtml?: boolean;
};

// Strips tags via the DOM so entities are decoded and words at block/cell
// boundaries stay separated; avoids the ReDoS-prone tag-stripping regex.
const htmlToPlainText = (html: string): string => {
	const doc = new DOMParser().parseFromString(html, 'text/html');
	doc.body.querySelectorAll('td, tr, p, br, li').forEach(element => {
		element.insertAdjacentText(
			element.tagName === 'BR' ? 'afterend' : 'beforeend',
			' ',
		);
	});
	return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
};

// Empty toolbar container satisfies table-better's init without rendering UI;
// the module must run so its clipboard matchers rebuild tables on load.
const READONLY_MODULES = {
	toolbar: {container: []},
	table: false,
	'table-better': {toolbarTable: false, menus: []},
};

const QuillReadOnlyView: React.FC<{html: string; className: string}> = ({
	html,
	className,
}) => {
	const quillRef = React.useRef<ReactQuill>(null);

	// table-better renders tables only when inserted as a Delta; feeding HTML via
	// the `value` prop uses setContents, which loops Parchment on tables.
	React.useEffect(() => {
		const editor = quillRef.current?.getEditor();
		if (!editor) {
			return;
		}
		editor.deleteText(0, editor.getLength(), 'silent');
		const delta = editor.clipboard.convert({html: html || ''});
		editor.updateContents(delta, 'silent');
	}, [html]);

	return (
		<ReactQuill
			ref={quillRef}
			readOnly={true}
			theme={'bubble'}
			className={className}
			defaultValue=''
			modules={READONLY_MODULES}
		/>
	);
};

export const RichtextDisplay: React.FC<RichtextDisplayProps> = ({
	value,
	limit = Infinity,
	removeHtml = false,
}) => {
	const quillClassName = mergeStyles({
		'.ql-editor': {
			padding: 0,
		},
	});

	if (removeHtml) {
		return <>{htmlToPlainText(value)}</>;
	}

	const editedValue = value
		.substring(0, limit)
		.concat(value.length > limit ? '...' : '');

	return <QuillReadOnlyView html={editedValue} className={quillClassName} />;
};
